import { auth } from './firebase';
import { Story } from '../Domain/Story';
import { StoryElement } from '../Domain/StoryElement';
import { config } from '../config';
import { EnhancementType } from '../Domain/enums/EnhancementType';
import { Artifact } from '../Domain/artifacts/Artifact';
import { StoryImageStyle } from '../Domain/enums/ArtifactPresets';
import { StoryElementType } from '../Domain/enums/StoryElementType';
import { AccountConfiguration } from '../Domain/Account/AccountConfiguration';
import { PhoneNumber } from '../Domain/Account/PhoneNumber';
import { PhoneNumberSlot } from '../Domain/Account/PhoneNumberSlot';
import { MarketplaceProduct } from '../Domain/Marketplace/MarketplaceProduct';
import { DashboardData } from '../Domain/DevOps/Dashboard';
import { PhoneNumberSlotType } from '../Domain/Account/PhoneNumberSlotType';
import { SystemEvent } from '../Domain/DevOps/SystemEvent';
import { Language } from '../Domain/Account/Language';
import { DevOpsUserSummary } from '../Domain/DevOps/DevOpsUserSummary';
import { DevOpsUsersSearchFilter } from '../Domain/DevOps/DevOpsUsersSearchFilter';
import type { BigQueryRequest, TaleCraftBigQueryDataSet } from '../Domain/DevOps/BigQuery';
import type { RecipesGetResponse } from './dto/RecipesGetResponse.ts';
import type { LaunchScreenConfiguration } from '../Domain/LaunchScreenConfiguration';
import type { VoiceProfile } from '../Domain/Voice/VoiceProfile';
import type { SantaClauseCreateRequest } from './dto/Santa';
import type { ImportVoucherBatchRequest, ImportedVoucherDefinition } from '../Domain/Voucher/VoucherModels';
import type { VoucherSummaryResponse, RecentVoucherUse } from '../Domain/Voucher/VoucherSummary';
import type { CreditCheckRequest, CreditCheckResponse } from '../Domain/Credits/CreditCheck';
import { getPendingVoucher } from '../utils/pendingVoucher';
import { VoiceSpeed } from '../Domain/enums/VoiceSpeed';
import { AdminScheduleCallRequest } from './Models/AdminScheduleCallRequest';
import { ScheduleCallRequest } from './Models/ScheduleCallRequest';
import { ScheduledCall } from '../Domain/Telephony/ScheduledCall';
export type { ScheduledCall };

export interface DevOpsAgentSummary {
    agentId: string;
    name: string;
    language: string;
    voiceId: string;
    createdAtUnixSecs?: number | null;
    lastCallTimeUnixSecs?: number | null;
    userId?: string | null;
}

export interface CreateAgentRequest {
    name: string;
    voiceSystemPrompt: string;
    firstMessage: string;
    voiceId: string;
    language: Language;
    speed: VoiceSpeed;
}

export interface CreateAgentResponse {
    agentId: string;
    link: string;
}

export interface VoiceSample {
    voiceId: string;
    voiceName: string;
    sampleUrl: string;
    isAvailable: boolean;
}

export interface VoiceSamplesResponse {
    voices: VoiceSample[];
    totalCount: number;
    lastUpdated: string;
}

interface DraftKnowledgeDocument {
    description: string;
    content: string;
}

/**
 * Request for the guest call scheduler checkout endpoint.
 */
export interface CallSchedulerCheckoutRequest {
    who: string;
    notes: string;
    phone: string;
    scheduledForUtcEpoch: number;
    email: string;
    language: Language;
    marketplaceProductId: string;
    marketplaceCharacterId: string;
}

/**
 * Response from the call scheduler checkout endpoint.
 */
export interface CallSchedulerCheckoutResponse {
    promiseCode: string;
    checkoutUrl: string;
}

interface UpsertStoryElementRequest {
    storyElement: StoryElement;
    removeKnowledgeIds?: string[];
}

interface PhoneNumberSlotDto {
    slot: PhoneNumberSlotType;
    phoneNumber: string;
    storyElementId?: string;
    characterName?: string;
}

const API_BASE_URL = config.API_BASE_URL;

interface VoiceChatSetup {
    agentId: string;
    voiceId: string;
    greeting: string;
    signedUrl: string;
    avatarImageUrl: string;
}

interface CheckoutSessionPayload {
    PriceId: string;
    PromotionCode?: string;
    FacebookData?: {
        Fbp: string | null;
        Fbc: string | null;
        PlanName: string;
        PlanPrice: number;
    };
    GoogleAnalyticsData?: {
        ClientId: string | undefined;
        SessionId: string | undefined;
    };
}

interface NewSubscriptionRequest {
    UserLanguage: Language;
}

export interface ApiError extends Error {
    response?: {
        status: number;
        data?: {
            error?: string;
            message?: string;
            errorMessage?: string;
        } | null;
    };
}

// Types for subscription plan change responses
interface SubscriptionPlanChangePreviewResponse {
    monthlyPrice: number;
    prorationCredit: number;
    payableNow: number;
    creditsAfterPurchase: number;
    creditsPerMonth: number;
    creditsRolledOver?: number;
}

interface SubscriptionPlanChangeExecutionResponse {
    requiresAction: boolean;
    clientSecret: string | null;
}

async function getAuthToken() {
    const user = auth.currentUser;
    if (!user) {
        throw new Error('User not authenticated [EC8]');
    }
    return user.getIdToken();
}

async function fetchWithAuth(endpoint: string, options: RequestInit = {}) {
    const token = await getAuthToken();
    const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers,
    };

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers,
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const error = new Error(`API request failed: ${response.statusText}`) as ApiError;
        error.response = {
            status: response.status,
            data: errorData
        };
        throw error;
    }

    // Handle empty responses for statuses like 204 No Content
    if (response.status === 204) {
        return Promise.resolve(null);
    }

    const text = await response.text();
    if (!text) {
        return null;
    }

    return JSON.parse(text);
}

async function fetchBlobWithAuth(endpoint: string, options: RequestInit = {}): Promise<Blob> {
    const token = await getAuthToken();
    const headers = {
        'Authorization': `Bearer ${token}`,
        ...options.headers,
    };

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers,
    });

    if (!response.ok) {
        const errorData = await response.text().catch(() => 'Failed to read error response');
        const error = new Error(`API request failed to fetch blob: ${response.statusText} - ${errorData}`) as ApiError;
        error.response = {
            status: response.status,
        };
        throw error;
    }

    return response.blob();
}

async function fetchPublic(endpoint: string, options: RequestInit = {}) {
    const headers = {
        'Content-Type': 'application/json',
        ...options.headers,
    };

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers,
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const error = new Error(`API request failed: ${response.statusText}`) as ApiError;
        error.response = {
            status: response.status,
            data: errorData
        };
        throw error;
    }

    return response.json();
}


interface EnhancementRequest {
    originalContent: string;
    enhancementType: EnhancementType;
    storyId: string;
}

interface EnhancementResponse {
    content: string;
}

interface FeedbackRequest {
    rating: number;
    text: string;
}

interface DiagnosticChatMessage {
    role: string;
    message: string;
}

interface DiagnosticsChatCompletionRequest {
    modelName: string;
    messages: DiagnosticChatMessage[];
}

interface DiagnosticsChatCompletionResponse {
    content: string;
}

interface RenameShareLinkRequest {
    currentShortUrlCode: string;
    newShortUrlCode: string;
}

export interface PublicCharacter {
  id: string;
  name: string;
  avatarUrl: string;
  dontTalkToStrangersAudioUrl?: string;
  publicAgentId?: string;
}

export interface PublicArtifact {
  title: string;
  url: string;
  downloadFilename: string;
}

export interface PublicData {
  storyId: string;
  storyTitle: string;
  story: {
    url: string;
    downloadFilename: string;
  } | null;
  audiobook: {
    url: string;
    title: string;
  } | null;
  coloringPage: {
    url: string;
    title: string;
    downloadFilename: string;
  } | null;
  song: {
    url: string;
    title: string;
  } | null;
  characters: PublicCharacter[] | null;
  marketplaceId?: string;
}

// Result type for treasure map generation
export interface TreasureMapGenerateResponse {
    imageDataBase64: string;
    contentType: string;
    url?: string;
}

// Result type for magic portrait generation
export interface MagicPortraitGenerateResponse {
    videoStorageUrl: string;
    contentType: string;
}

// Result type for morning plan generation
export interface MorningPlanGenerateResponse {
    imageDataBase64: string;
    fileName: string;
}

class Api {
    private abortControllers: Map<string, AbortController> = new Map();

    async getAuthToken() {
        return getAuthToken();
    }

    devops = {
        getDashboard: (): Promise<DashboardData> => fetchWithAuth('/api/DevOps/dashboard'),
        getSystemEvents: (params: { userId?: string; take?: number }): Promise<SystemEvent[]> => {
            let endpoint = '/api/DevOps/system-events';
            if (params.userId) {
                endpoint = `${endpoint}/${params.userId}`;
            } else if (params.take) {
                endpoint = `${endpoint}?take=${params.take}`;
            }
            return fetchWithAuth(endpoint);
        },
        getUsers: (params?: { email?: string; filter?: DevOpsUsersSearchFilter; limit?: number }): Promise<DevOpsUserSummary[]> => {
            // Backend endpoint: POST /api/DevOps/users with JSON body
            const body: { email?: string; filter?: DevOpsUsersSearchFilter; limit?: number } = {};

            if (params?.email) {
                body.email = params.email.trim();
            } else {
                if (params?.filter !== undefined) {
                    body.filter = params.filter;
                }
                if (typeof params?.limit === 'number') {
                    body.limit = Math.max(1, Math.min(500, Math.floor(params.limit)));
                }
            }

            return fetchWithAuth('/api/DevOps/users', {
                method: 'POST',
                body: JSON.stringify(body)
            });
        },
        backfillSubscriptionEmailDomain: (maxToScan: number = 5000): Promise<unknown> => {
            const endpoint = `/api/DevOps/data-migrations/backfill-subscription-email-domain?maxToScan=${encodeURIComponent(maxToScan)}`;
            return fetchWithAuth(endpoint, { method: 'POST' });
        },
        backfillBannerAndTaglines: (): Promise<unknown> => {
            const endpoint = `/api/DevOps/data-migrations/backfill-banner-and-taglines`;
            return fetchWithAuth(endpoint, { method: 'POST' });
        },
        bigQuery: (request: BigQueryRequest): Promise<TaleCraftBigQueryDataSet> => {
            return fetchWithAuth('/api/DevOps/bigquery', {
                method: 'POST',
                body: JSON.stringify(request)
            });
        },
        normalizeMarketplaceLanguages: (): Promise<unknown> => {
            const endpoint = `/api/DevOps/data-migrations/normalize-marketplace-languages`;
            return fetchWithAuth(endpoint, { method: 'POST' });
        },
        elevenLabsAgentsUpdate: (agentIds?: string[]): Promise<unknown> => {
            const endpoint = `/api/DevOps/data-migrations/elevenlabs-agents-update`;
            return fetchWithAuth(endpoint, { 
                method: 'POST',
                body: JSON.stringify(agentIds ?? null)
            });
        },
        completeReminder: async (reminderId: string): Promise<void> => {
            const token = await getAuthToken();
            const response = await fetch(`${API_BASE_URL}/api/DevOps/reminders/${reminderId}/complete`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                const errorData = await response.text().catch(() => null);
                const error = new Error(`API request failed: ${response.statusText}`) as ApiError;
                error.response = {
                    status: response.status,
                    data: errorData ? { error: errorData, message: errorData } : null
                };
                throw error;
            }

            // Handle successful responses - don't try to parse JSON if empty
            if (response.status === 204 || response.headers.get('content-length') === '0') {
                return Promise.resolve();
            }

            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                try {
                    await response.json();
                } catch {
                    // Ignore JSON parsing errors for successful responses
                }
            }
            
            return Promise.resolve();
        },
        scheduleAndExecutePermanentAgentCall: (request: AdminScheduleCallRequest): Promise<void> => {
            return fetchWithAuth('/api/DevOps/test-call-scheduler/schedule-and-execute-permanent-agent', {
                method: 'POST',
                body: JSON.stringify(request)
            });
        },
        cleanupStuckNumbers: (): Promise<void> => {
            return fetchWithAuth('/api/DevOps/telephony/cleanup-stuck-numbers', {
                method: 'POST'
            });
        },
        getAgents: (): Promise<DevOpsAgentSummary[]> => fetchWithAuth('/api/DevOps/agents'),
        deleteScheduledCall: (callId: string): Promise<void> => {
            return fetchWithAuth(`/api/DevOps/scheduled-calls/${callId}`, {
                method: 'DELETE'
            });
        },
    };

    public = {
        getContent: async (shortCode: string): Promise<PublicData> => {
            try {
                return await fetchPublic(`/api/shared/code/${shortCode}`);
            } catch (error) {
                // Return empty object for 404 errors
                if (error instanceof Error && 'response' in error) {
                    const apiError = error as ApiError;
                    if (apiError.response?.status === 404) {
                        return {
                            storyId: '',
                            storyTitle: '',
                            story: null,
                            audiobook: null,
                            coloringPage: null,
                            song: null,
                            characters: [],
                            marketplaceId: undefined
                        };
                    }
                }
                throw error;
            }
        },
    };

    subscriptions = {
        setupNewDefault: (payload: NewSubscriptionRequest) => fetchWithAuth('/api/Subscriptions/SetupNewDefaultSubscription', {
            method: 'POST',
            body: JSON.stringify(payload)
        }),
        createCheckoutSession: (priceId: string, trackingData?: { 
            facebookData?: { fbp: string | null; fbc: string | null; planName: string; planPrice: number }; 
            googleData?: { clientId: string | undefined; sessionId: string | undefined; } | null 
        }) => {
            // Transform the tracking data to match backend expectations
            const payload: CheckoutSessionPayload = { PriceId: priceId };
            
            const pendingVoucher = getPendingVoucher();
            if (pendingVoucher && pendingVoucher.trim().toUpperCase().startsWith('TC')) {
                payload.PromotionCode = pendingVoucher.trim();
            }

            if (trackingData?.facebookData) {
                payload.FacebookData = {
                    Fbp: trackingData.facebookData.fbp,
                    Fbc: trackingData.facebookData.fbc,
                    PlanName: trackingData.facebookData.planName,
                    PlanPrice: trackingData.facebookData.planPrice
                };
            }
            
            if (trackingData?.googleData) {
                payload.GoogleAnalyticsData = {
                    ClientId: trackingData.googleData.clientId,
                    SessionId: trackingData.googleData.sessionId
                };
            }
            
            return fetchWithAuth('/api/Subscriptions/Checkout', {
                method: 'POST',
                body: JSON.stringify(payload)
            });
        },
        changePlan: <T extends boolean | undefined = undefined>(priceId: string, preview: T) => {
            type ResponseType = T extends true ? SubscriptionPlanChangePreviewResponse : SubscriptionPlanChangeExecutionResponse;
            return fetchWithAuth('/api/Subscriptions/ChangePlan', {
                method: 'POST',
                body: JSON.stringify({ priceId, preview })
            }) as Promise<ResponseType>;
        }
    };

    credits = {
        check: (request: CreditCheckRequest): Promise<CreditCheckResponse> =>
            fetchWithAuth('/credits/check', {
                method: 'POST',
                body: JSON.stringify(request)
            })
    };

    vouchers = {
        redeem: (code: string): Promise<void> =>
            fetchWithAuth('/api/vouchers/redeem', {
                method: 'POST',
                body: JSON.stringify({ Code: code.trim() })
            }),
        getRecentUses: (limit?: number): Promise<RecentVoucherUse[]> => {
            const query = limit ? `?limit=${encodeURIComponent(limit)}` : '';
            return fetchWithAuth(`/api/vouchers/recent${query}`);
        }
    };

    story = {
        get: (id: string) => fetchWithAuth(`/story/get/${id}`),
        delete: (id: string) => fetchWithAuth(`/story/delete/${id}`, { method: 'DELETE' }),
        upsert: (story: Story) => fetchWithAuth('/story/upsert', {
            method: 'POST',
            body: JSON.stringify(story),
        })
    };

    storyElement = {
        get: (id: string) => fetchWithAuth(`/storyElement/get/${id}`),
        delete: (id: string) => fetchWithAuth(`/storyElement/delete/${id}`, { method: 'DELETE' }),
        upsert: (element: StoryElement, avatarFile?: File | null, newKnowledge?: DraftKnowledgeDocument[], removeKnowledgeIds?: string[]) => {
            const formData = new FormData();
            
            // Create the wrapper request object
            const requestData: UpsertStoryElementRequest = {
                storyElement: element,
                removeKnowledgeIds: removeKnowledgeIds
            };
            
            // The backend will parse the request data JSON string
            formData.append('requestData', JSON.stringify(requestData));
            
            if (newKnowledge && newKnowledge.length > 0) {
                formData.append('newKnowledgeData', JSON.stringify(newKnowledge));
            }

            if (avatarFile) {
                formData.append('avatarFile', avatarFile);
            }

            // Always use FormData, Content-Type is set automatically by the browser
            return (async () => {
                const token = await getAuthToken();
                const response = await fetch(`${API_BASE_URL}/storyElement/upsert`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        // No 'Content-Type' here; browser sets it for FormData
                    },
                    body: formData,
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => null);
                    const error = new Error(`API request failed: ${response.statusText}`) as ApiError;
                    error.response = {
                        status: response.status,
                        data: errorData
                    };
                    throw error;
                }
                return response.json();
            })();
        },
        generateAvatar: (name: string, description: string, storyElementType: StoryElementType, storyId?: string): Promise<{ imageDataBase64: string, contentType: string }> => 
            fetchWithAuth('/storyElement/generate-avatar', {
                method: 'POST',
                body: JSON.stringify({ name, description, StoryElementType: storyElementType, storyId }),
        }),

        // --- Instructions for Backend Developer ---
        // The following methods are placeholders for knowledge management.

        /**
         * Uploads a new knowledge document (.txt or .md file) and associates it with a character.
         * The backend should read the file, create partitions, calculate the MD5 hash for the ID,
         * create a new KnowledgeDocument of type CustomKnowledge, and save it in the character's
         * /knowledgeDocuments subcollection.
         * Endpoint suggestion: POST /api/storyElement/{storyElementId}/knowledge/upload
         */
        uploadKnowledge: (storyElementId: string, file: File, description: string) => {
            const formData = new FormData();
            formData.append('knowledgeFile', file);
            formData.append('description', description);
            
            // This is a placeholder and will need a real implementation.
            console.log("API Call: uploadKnowledge", { storyElementId, description });
            return Promise.reject(new Error("uploadKnowledge not implemented"));
            /*
            return (async () => {
                const token = await getAuthToken();
                const response = await fetch(`${API_BASE_URL}/api/storyElement/${storyElementId}/knowledge/upload`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` },
                    body: formData,
                });
                if (!response.ok) throw new Error('Failed to upload knowledge');
                return response.json();
            })();
            */
        },

        /**
         * Unlinks and deletes a knowledge document from a character.
         * Endpoint suggestion: DELETE /api/storyElement/{storyElementId}/knowledge/{knowledgeId}
         */
        removeKnowledgeFromCharacter: (storyElementId: string, knowledgeId: string) => {
            // This is a placeholder and will need a real implementation.
            console.log("API Call: removeKnowledgeFromCharacter", { storyElementId, knowledgeId });
            return Promise.reject(new Error("removeKnowledgeFromCharacter not implemented"));
            /*
            return fetchWithAuth(`/api/storyElement/${storyElementId}/knowledge/${knowledgeId}`, {
                method: 'DELETE'
            });
            */
        },

        getKnowledgeDocument: (storyElementId: string, knowledgeDocumentId:string): Promise<Blob> => 
            fetchBlobWithAuth(`/storyElement/${storyElementId}/knowledge/${knowledgeDocumentId}`, {
                method: 'GET'
            })
    };

    elevenLabs = {
        getVoiceSamples: (): Promise<VoiceSamplesResponse> => fetchWithAuth('/elevenlabs/voice-samples'),
    };

    // NEW Audiobook API section for voice samples
    audiobook = {
        /**
         * Gets available voice samples for audiobook voice styles.
         */
        getVoiceSamples: (): Promise<VoiceSamplesResponse> => fetchWithAuth('/audiobook/voice-samples'),
    };

    // Voice Profiles
    voiceProfiles = {
        /**
         * Fetch all supported voice profiles.
         * Backend: GET /api/VoiceProfiles/all
         */
        getAll: (): Promise<VoiceProfile[]> => fetchWithAuth('/api/VoiceProfiles/all', { method: 'GET' })
    };

    artifacts = {
        get: (id: string) => fetchWithAuth(`/artifacts/${id}`),
        updateParent: (artifactId: string, storyId: string, newParentBagId: string | null): Promise<void> => {
            return fetchWithAuth(`/artifacts/${artifactId}/parent`, {
                method: 'PUT',
                body: JSON.stringify({ StoryId: storyId, NewParentBagId: newParentBagId }),
            });
        },
        delete: (storyId: string, artifactId: string): Promise<void> => {
            return fetchWithAuth(`/story/${storyId}/artifact/${artifactId}`, { method: 'DELETE' });
        },
        create: (artifact: Artifact): Promise<{ artifactId: string; storyId: string }> => {
            return fetchWithAuth('/artifacts/create', {
                method: 'POST',
                body: JSON.stringify(artifact),
            });
        },
        generate: async (artifact: Artifact, isRegeneration: boolean = false, useFastModel: boolean = false) => {
            const controller = new AbortController();
            this.abortControllers.set(artifact.processTrackingId!, controller);

            try {
                const request = {
                    Artifact: artifact,
                    Regenerate: isRegeneration,
                    UseFastModel: useFastModel
                };

                await fetchWithAuth('/artifacts/generate', {
                    method: 'POST',
                    body: JSON.stringify(request),
                    signal: controller.signal
                });
            } catch (error) {
                if (error instanceof DOMException && error.name === 'AbortError') {
                    const cancelError = new Error('Generation cancelled') as ApiError;
                    cancelError.response = {
                        status: 499, // Client Closed Request
                        data: {
                            error: 'cancelled',
                            message: 'Generation was cancelled by the user'
                        }
                    };
                    throw cancelError;
                }
                throw error;
            } finally {
                this.abortControllers.delete(artifact.processTrackingId!);
            }
        }
    };

    enhancements = {
        enhance: (request: EnhancementRequest): Promise<EnhancementResponse> => 
            fetchWithAuth('/enhancements/enhance', {
                method: 'POST',
                body: JSON.stringify(request)
            })
    };

    chat = {
        setup: (storyId: string) => fetchWithAuth(`/api/Chat/${storyId}/Setup`, {
            method: 'POST'
        }),
        processTurn: (chatId: string, characterId: string, storyId: string, userMessage: string) =>
            fetchWithAuth(`/api/Chat/${chatId}/Turn`, {
                method: 'POST',
                body: JSON.stringify({ characterId, storyId, userMessage })
            }),
        startVoiceChat: (storyId: string, characterId: string, speed?: VoiceSpeed): Promise<VoiceChatSetup> => 
            fetchWithAuth(`/api/Chat/voice-chat/start/${storyId}/${characterId}`, {
                method: 'POST',
                body: speed ? JSON.stringify({ speed }) : undefined
            })
    };

    feedback = {
        submit: (request: FeedbackRequest): Promise<void> =>
            fetchWithAuth('/api/Feedback/Submit', {
                method: 'POST',
                body: JSON.stringify(request)
            })
    };

    diagnostics = {
        chatCompletion: (request: DiagnosticsChatCompletionRequest): Promise<DiagnosticsChatCompletionResponse> =>
            fetchWithAuth('/api/Diagnostics/chat-completion', {
                method: 'POST',
                body: JSON.stringify(request)
            }),
        getTraceAttachment: (location: { transactionId: string; spanId: string; attachmentId: string; fileName: string; }): Promise<Blob> => {
            const endpoint = `/api/Diagnostics/tracing/attachments/${location.transactionId}/${location.spanId}/${location.attachmentId}/${encodeURIComponent(location.fileName)}`;
            return fetchBlobWithAuth(endpoint, {
                method: 'GET'
            });
        }
    };

    // --- Recipes ---
    recipes = {
        /**
         * Fetches Launchpad recipe suggestions.
         * Backend: POST /recipes/get, body { ClientLocalTimeEpoch: number }
         */
        get: (clientLocalTimeEpoch: number = Math.floor(Date.now() / 1000)) =>
            fetchWithAuth('/recipes/get', {
                method: 'POST',
                body: JSON.stringify({ ClientLocalTimeEpoch: clientLocalTimeEpoch })
            }) as Promise<RecipesGetResponse>,
    };

    // --- Santa (Red Phone) ---
    santa = {
        /**
         * Creates a Santa Clause call request.
         * Backend: POST /santa/create
         */
        create: (request: SantaClauseCreateRequest): Promise<void> =>
            fetchWithAuth('/santa/create', {
                method: 'POST',
                body: JSON.stringify(request)
            })
    };

    // --- Launch Screen ---
    launchScreen = {
        /**
         * Fetches configuration for launch screen featured artifacts.
         * Backend: GET /launch-screen-configuration
         */
        getConfiguration: (): Promise<LaunchScreenConfiguration> =>
            fetchWithAuth('/launch-screen-configuration', {
                method: 'GET'
            }),
    };

    maintenance = {
        seedCategories: (): Promise<void> =>
            fetchWithAuth('/api/Maintenance/seed-categories', {
                method: 'POST'
            }),
        seedStripeConfiguration: (): Promise<void> =>
            fetchWithAuth('/api/Maintenance/seed-stripe-configuration', {
                method: 'POST'
            }),
        seedReminders: (): Promise<void> =>
            fetchWithAuth('/api/DevOps/seed-reminders', {
                method: 'POST'
            }),
        seedTelephony: (): Promise<void> =>
            fetchWithAuth('/api/DevOps/seed-telephony', {
                method: 'POST'
            }),
        renameShareLink: (request: RenameShareLinkRequest): Promise<void> =>
            fetchWithAuth('/api/Maintenance/share-links/rename', {
                method: 'POST',
                body: JSON.stringify(request)
            }),
        backfillCompanionAgentLanguages: (): Promise<void> =>
            fetchWithAuth('/api/Maintenance/companion-agents/language/backfill', {
                method: 'POST'
            }),
        backfillDontTalkToStrangersVoiceSamples: (): Promise<void> =>
            fetchWithAuth('/api/Maintenance/voice-samples/dont-talk-to-strangers/backfill', {
                method: 'POST'
            }),
        importVouchers: (request: ImportVoucherBatchRequest, files: File[]): Promise<ImportedVoucherDefinition[]> => {
            if (!files.length) {
                return Promise.reject(new Error('At least one file containing voucher codes is required.'));
            }

            const formData = new FormData();
            formData.append('metadata', JSON.stringify(request));
            files.forEach(file => formData.append('files', file));

            return (async () => {
                const token = await getAuthToken();
                const response = await fetch(`${API_BASE_URL}/api/vouchers/import`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                    body: formData
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => null);
                    const error = new Error(`API request failed: ${response.statusText}`) as ApiError;
                    error.response = {
                        status: response.status,
                        data: errorData ?? undefined
                    };
                    throw error;
                }

                return response.json() as Promise<ImportedVoucherDefinition[]>;
            })();
        },
        getVoucherSummary: (recentLimit?: number): Promise<VoucherSummaryResponse> => {
            const hasLimit = typeof recentLimit === 'number' && !Number.isNaN(recentLimit);
            const sanitizedLimit = hasLimit ? Math.max(1, Math.min(200, Math.floor(recentLimit!))) : undefined;
            const query = sanitizedLimit ? `?recentLimit=${encodeURIComponent(sanitizedLimit)}` : '';
            return fetchWithAuth(`/api/Maintenance/vouchers/summary${query}`);
        },
        createAgent: (request: CreateAgentRequest): Promise<CreateAgentResponse> => 
            fetchWithAuth('/api/Maintenance/create-agent', {
                method: 'POST',
                body: JSON.stringify(request)
            }),
    };

    morningPlan = {
        /**
         * Generates a morning plan image.
         * Backend: POST /morning-plan/generate
         * Body: { styleDescription: string, steps: Array<{ description: string }> }
         */
        generate: (request: { styleDescription: string; steps: Array<{ description: string }> }): Promise<MorningPlanGenerateResponse> => {
            return fetchWithAuth('/morning-plan/generate', {
                method: 'POST',
                body: JSON.stringify(request)
            });
        }
    };

    notifications = {
        acknowledge: (notificationId: string): Promise<void> =>
            fetchWithAuth('/notifications/acknowledge', {
                method: 'POST',
                body: JSON.stringify({ notificationId })
            }),
        post: (userId: string, message: string): Promise<void> =>
            fetchWithAuth('/notifications/send', {
                method: 'POST',
                body: JSON.stringify({ userId, message })
            })
    };

    marketplace = {
        publish: (bagId: string): Promise<MarketplaceProduct> =>
            fetchWithAuth('/api/Marketplace/Publish', {
                method: 'POST',
                body: JSON.stringify({ bagId })
            }),
        publishProduct: (product: MarketplaceProduct): Promise<MarketplaceProduct> =>
            fetchWithAuth('/api/Marketplace/PublishProduct', {
                method: 'POST',
                body: JSON.stringify({ product })
            }),
        acquireProduct: (productId: string, targetBagId: string | null = null): Promise<void> =>
            fetchWithAuth('/api/Marketplace/Acquire', {
                method: 'POST',
                body: JSON.stringify({ productId, targetBagId })
            }),
        unpublishProduct: (productId: string): Promise<MarketplaceProduct> =>
            fetchWithAuth('/api/Marketplace/UnpublishProduct', {
                method: 'POST',
                body: JSON.stringify({ productId })
            }),
        abandonProduct: (productId: string): Promise<{ success: boolean; message: string }> =>
            fetchWithAuth('/api/Marketplace/AbandonProduct', {
                method: 'POST',
                body: JSON.stringify({ productId })
            })
    };

    // --- Treasure Maps ---
    treasureMaps = {
        /**
         * Generates a treasure map image from a user-provided floor plan or layout image.
         * Backend: POST /api/treasure-maps/generate
         * Body: multipart/form-data with fields { file: File, styleRef: string }
         */
        generate: (sourceImage: File, styleRef: string): Promise<TreasureMapGenerateResponse> => {
            const formData = new FormData();
            formData.append('file', sourceImage);
            formData.append('styleRef', styleRef ?? '');

            return (async () => {
                const token = await getAuthToken();
                const response = await fetch(`${API_BASE_URL}/treasure-maps/generate`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                    body: formData,
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => null);
                    const error = new Error(`API request failed: ${response.statusText}`) as ApiError;
                    error.response = {
                        status: response.status,
                        data: errorData
                    };
                    throw error;
                }
                return response.json();
            })();
        }
    };

    // --- Magic Portraits ---
    magicPortraits = {
        /**
         * Generates a magic portrait video from a user-provided image and prompt.
         * Backend: POST /magic-portraits/generate
         * Body: multipart/form-data with fields { image: File, prompt: string, duration: string }
         */
        generate: (imageFile: File, prompt: string, duration: number): Promise<MagicPortraitGenerateResponse> => {
            const formData = new FormData();
            formData.append('image', imageFile);
            formData.append('prompt', prompt);
            formData.append('duration', duration.toString());

            return (async () => {
                const token = await getAuthToken();
                const response = await fetch(`${API_BASE_URL}/magic-portraits/generate`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                    body: formData,
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => null);
                    const error = new Error(`API request failed: ${response.statusText}`) as ApiError;
                    error.response = {
                        status: response.status,
                        data: errorData
                    };
                    throw error;
                }
                return response.json();
            })();
        }
    };

    // --- Games Generator ---
    gamesGenerator = {
        /**
         * Generates game ideas based on participant count, indoor/outdoor preference, optional instructions, and youngest age.
         * Backend: POST /games-generator/generate
         * Body: { participantCount: number, isIndoor: boolean, freeTextInstruction?: string, youngestAgeYears: number }
         */
        generate: (request: { participantCount: number; isIndoor: boolean; freeTextInstruction?: string; youngestAgeYears: number }): Promise<{ games: Array<{ name: string; rules: string; imageUrl: string }> }> => {
            return fetchWithAuth('/games-generator/generate', {
                method: 'POST',
                body: JSON.stringify(request)
            });
        }
    };

    // --- Scheduled Messages ---
    scheduledMessages = {
        /**
         * Gets the current user's scheduled SMS messages.
         * Backend: GET /scheduled-messages
         * Response: { instructions?: string, schedule: Array<{ day: number, time: string }> }
         */
        get: (): Promise<{ instructions?: string; schedule: Array<{ day: number; time: string }> }> =>
            fetchWithAuth('/scheduled-messages', {
                method: 'GET'
            }),
        /**
         * Creates or updates scheduled SMS messages.
         * Backend: POST /scheduled-messages
         * Body: { instructions?: string, schedule: Array<{ day: number, time: string }> }
         */
        save: (request: { instructions?: string; schedule: Array<{ day: number; time: string }> }): Promise<void> => {
            return fetchWithAuth('/scheduled-messages', {
                method: 'POST',
                body: JSON.stringify(request)
            });
        }
    };

    phoneCalls = {
        list: (): Promise<ScheduledCall[]> => 
            fetchWithAuth('/api/scheduled-calls/member', {
                method: 'GET'
            }),
        delete: (id: string): Promise<void> => 
            fetchWithAuth(`/api/scheduled-calls/member/${id}`, {
                method: 'DELETE'
            }),
        schedule: (request: ScheduleCallRequest): Promise<void> => 
            fetchWithAuth('/api/scheduled-calls/member/schedule', {
                method: 'POST',
                body: JSON.stringify(request)
            }),
        retry: (request: { callId: string, scheduleAtUtcEpoch: number }): Promise<void> =>
            fetchWithAuth('/api/scheduled-calls/member/retry', {
                method: 'POST',
                body: JSON.stringify(request)
            })
    };

    /**
     * Call scheduler endpoints for unregistered/guest users.
     * Uses Stripe checkout flow and creates accounts automatically.
     */
    callScheduler = {
        /**
         * Initiates a checkout session for scheduling a call as a guest.
         * Returns a promise code and checkout URL for Stripe payment.
         */
        checkout: async (request: CallSchedulerCheckoutRequest): Promise<CallSchedulerCheckoutResponse> => {
            const response = await fetch(`${API_BASE_URL}/api/scheduled-calls/guest/checkout`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(request)
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                const error = new Error(`API request failed: ${response.statusText}`) as ApiError;
                error.response = {
                    status: response.status,
                    data: errorData
                };
                throw error;
            }

            return response.json();
        }
    };

    accountConfiguration = {
        get: (): Promise<AccountConfiguration> => 
            fetchWithAuth('/account-configuration', {
                method: 'GET'
            }),
        
        update: (configuration: AccountConfiguration): Promise<AccountConfiguration> =>
            fetchWithAuth('/account-configuration', {
                method: 'PUT',
                body: JSON.stringify(configuration)
            }),
        
        updatePreferredLanguage: (language: Language): Promise<AccountConfiguration> =>
            fetchWithAuth('/account-configuration/preferred-language', {
                method: 'PUT',
                body: JSON.stringify(language)
            }),
        
        updatePhoneNumber: (phoneNumber: PhoneNumber): Promise<AccountConfiguration> =>
            fetchWithAuth('/account-configuration/phone-number', {
                method: 'PUT',
                body: JSON.stringify({
                    ...phoneNumber,
                    countryCode: phoneNumber.countryCode.replace('+', '')
                })
            }),
            
        updatePhoneNumberSlot: (slot: PhoneNumberSlot): Promise<PhoneNumberSlot> =>
            fetchWithAuth('/account-configuration/phone-number-slot', {
                method: 'PUT',
                body: JSON.stringify(slot)
            }),
        
        getPhoneNumberSlots: (): Promise<PhoneNumberSlotDto[]> =>
            fetchWithAuth('/account-configuration/phone-number-slots', {
                method: 'GET'
            }),
        
        unassignPhoneNumberSlot: (slot: string): Promise<PhoneNumberSlot> =>
            fetchWithAuth('/account-configuration/phone-number-slot', {
                method: 'PUT',
                body: JSON.stringify({
                    slot: slot,
                    storyElementId: ""
                })
            })
    };

    /**
     * Phone verification API methods for SMS OTP verification (authenticated users).
     */
    phoneVerification = {
        /**
         * Send a verification code to the specified phone number.
         * @param phoneNumber Phone number in E.164 format (e.g., +48123456789)
         */
        sendCode: (phoneNumber: string): Promise<{ success: boolean; status?: string; errorMessage?: string }> =>
            fetchWithAuth('/api/phone-verification/send', {
                method: 'POST',
                body: JSON.stringify({ phoneNumber })
            }),

        /**
         * Verify the code entered by the user.
         * @param phoneNumber Phone number in E.164 format (e.g., +48123456789)
         * @param code The verification code entered by the user
         */
        verifyCode: (phoneNumber: string, code: string): Promise<{ isValid: boolean; status: string; errorMessage?: string }> =>
            fetchWithAuth('/api/phone-verification/verify', {
                method: 'POST',
                body: JSON.stringify({ phoneNumber, code })
            })
    };

    /**
     * Phone verification API methods for guests (no authentication required).
     */
    phoneVerificationPublic = {
        /**
         * Send a verification code to the specified phone number (public/guest endpoint).
         * @param phoneNumber Phone number in E.164 format (e.g., +48123456789)
         */
        sendCode: (phoneNumber: string): Promise<{ success: boolean; status?: string; errorMessage?: string }> =>
            fetchPublic('/api/phone-verification/send', {
                method: 'POST',
                body: JSON.stringify({ phoneNumber })
            }),

        /**
         * Verify the code entered by the user (public/guest endpoint).
         * @param phoneNumber Phone number in E.164 format (e.g., +48123456789)
         * @param code The verification code entered by the user
         */
        verifyCode: (phoneNumber: string, code: string): Promise<{ isValid: boolean; status: string; errorMessage?: string }> =>
            fetchPublic('/api/phone-verification/verify', {
                method: 'POST',
                body: JSON.stringify({ phoneNumber, code })
            })
    };

    /**
     * Child Profile management API.
     * Profiles are stored in users-{env}/{userId}/childProfiles sub-collection.
     */
    childProfile = {
        /**
         * Create or update a child profile.
         * @param profile The profile data to upsert (id optional for create)
         * @param avatarFile Optional avatar image file
         */
        upsert: (profile: { id?: string; name: string; notes?: string }, avatarFile?: File | null): Promise<{ id: string }> => {
            const formData = new FormData();
            formData.append('profileData', JSON.stringify(profile));
            
            if (avatarFile) {
                formData.append('avatarFile', avatarFile);
            }

            return (async () => {
                const token = await getAuthToken();
                const response = await fetch(`${API_BASE_URL}/api/child-profile/upsert`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                    body: formData,
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => null);
                    const error = new Error(`API request failed: ${response.statusText}`) as ApiError;
                    error.response = {
                        status: response.status,
                        data: errorData
                    };
                    throw error;
                }
                return response.json();
            })();
        },

        /**
         * Delete a child profile.
         * @param profileId The ID of the profile to delete
         */
        delete: (profileId: string): Promise<void> =>
            fetchWithAuth(`/api/child-profile/${profileId}`, { method: 'DELETE' }),
    };

    cancelGeneration(processTrackingId: string): void {
        const controller = this.abortControllers.get(processTrackingId);
        if (controller) {
            controller.abort();
            this.abortControllers.delete(processTrackingId);
        }
    }

    async regenerateImage(artifactId: string, storyId: string, description: string, tag: string, imageStyle: StoryImageStyle): Promise<void> {
        const request = {
            tag: tag,
            revisedImageSpecification: description,
            storyId: storyId,
            imageStyle: imageStyle
        };
              
        return fetchWithAuth(`/artifacts/${artifactId}/regenerate-image`, {
            method: 'PUT',
            body: JSON.stringify(request)
        });
    }

    async getShareLink(bagId: string): Promise<{ shortLink: string }> {
        const response: { shortCode: string } = await fetchWithAuth(`/api/sharing/link/${bagId}`, {
            method: 'POST'
        });
        const fullUrl = `${window.location.origin}/view/${response.shortCode}`;
        return { shortLink: fullUrl };
    }

    async removeShareLink(bagId: string): Promise<void> {
        return fetchWithAuth(`/api/sharing/link/${bagId}`, {
            method: 'DELETE'
        });
    }
}

export const api = new Api();
